import { type FormEvent, useMemo, useState } from 'react';
import type { Api } from '../api.js';
import {
  type Account,
  type Journal,
  type TaxCode,
  type TrackingDimension,
  today,
} from './types.js';
import { useOrgData } from './useOrgData.js';

interface DraftLine {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
  taxCode: string;
  tracking1: string;
  description: string;
}

let nextKey = 1;
const blankLine = (): DraftLine => ({
  key: nextKey++,
  accountId: '',
  debit: '',
  credit: '',
  taxCode: '',
  tracking1: '',
  description: '',
});

/** Sums decimal strings in cents with BigInt: no floats, even in the browser. */
function cents(value: string): bigint {
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(value.trim());
  if (!m || (!m[1] && !m[2])) return 0n;
  return BigInt(m[1] || '0') * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0');
}
function fmt(c: bigint): string {
  const neg = c < 0n;
  const abs = (neg ? -c : c).toString().padStart(3, '0');
  return `${neg ? '-' : ''}${abs.slice(0, -2)}.${abs.slice(-2)}`;
}

export function Journals({ api, orgId }: { api: Api; orgId: string }) {
  const journals = useOrgData<{ journals: Journal[] }>(api, orgId, '/journals?limit=100');
  const accounts = useOrgData<{ accounts: Account[] }>(api, orgId, '/accounts');
  const taxCodes = useOrgData<{ taxCodes: TaxCode[] }>(api, orgId, '/tax-codes');
  const tracking = useOrgData<{ dimensions: TrackingDimension[] }>(api, orgId, '/tracking');
  const product = tracking.data?.dimensions.find((d) => d.position === 1);
  const accountById = useMemo(
    () => new Map((accounts.data?.accounts ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );

  const [date, setDate] = useState(today());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine(), blankLine()]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const totalDebit = lines.reduce((s, l) => s + cents(l.debit), 0n);
  const totalCredit = lines.reduce((s, l) => s + cents(l.credit), 0n);

  const update = (key: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  async function post(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', '/journals', {
        date,
        memo,
        lines: lines
          .filter((l) => l.accountId && (cents(l.debit) || cents(l.credit)))
          .map((l) => ({
            accountId: l.accountId,
            amount: fmt(cents(l.debit) - cents(l.credit)),
            ...(l.taxCode ? { taxCode: l.taxCode } : {}),
            ...(l.tracking1 ? { tracking1: l.tracking1 } : {}),
            ...(l.description ? { description: l.description } : {}),
          })),
      });
      setMemo('');
      setLines([blankLine(), blankLine()]);
      await journals.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post the entry.');
    } finally {
      setBusy(false);
    }
  }

  async function reverse(id: string) {
    setError('');
    try {
      await api.send(orgId, 'POST', `/journals/${id}/reverse`, { date: today() });
      await journals.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reverse the entry.');
    }
  }

  if (accounts.data && accounts.data.accounts.length === 0) {
    return <p>Set up the chart of accounts first.</p>;
  }

  return (
    <section className="stack">
      <h2>Manual journal</h2>
      <form onSubmit={post} className="stack">
        <div className="title-row">
          <label className="inline">
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="inline grow">
            Memo
            <input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </label>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
              <th>Tax code</th>
              {product && <th>{product.name}</th>}
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>
                  <select
                    value={l.accountId}
                    onChange={(e) => {
                      const account = accountById.get(e.target.value);
                      update(l.key, {
                        accountId: e.target.value,
                        taxCode: account?.default_tax_code ?? '',
                      });
                    }}
                  >
                    <option value="">Choose</option>
                    {accounts.data?.accounts
                      .filter((a) => !a.archived)
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  <input
                    className="num"
                    inputMode="decimal"
                    value={l.debit}
                    onChange={(e) => update(l.key, { debit: e.target.value, credit: '' })}
                  />
                </td>
                <td>
                  <input
                    className="num"
                    inputMode="decimal"
                    value={l.credit}
                    onChange={(e) => update(l.key, { credit: e.target.value, debit: '' })}
                  />
                </td>
                <td>
                  <select
                    value={l.taxCode}
                    onChange={(e) => update(l.key, { taxCode: e.target.value })}
                  >
                    <option value="">None</option>
                    {taxCodes.data?.taxCodes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code}
                      </option>
                    ))}
                  </select>
                </td>
                {product && (
                  <td>
                    <select
                      value={l.tracking1}
                      onChange={(e) => update(l.key, { tracking1: e.target.value })}
                    >
                      <option value="">None</option>
                      {product.values.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td>
                  <input
                    value={l.description}
                    onChange={(e) => update(l.key, { description: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <button
                  type="button"
                  className="link"
                  onClick={() => setLines((ls) => [...ls, blankLine()])}
                >
                  Add a line
                </button>
              </td>
              <td className="num">{fmt(totalDebit)}</td>
              <td className="num">{fmt(totalCredit)}</td>
              <td
                colSpan={product ? 3 : 2}
                className={totalDebit === totalCredit ? 'muted' : 'error'}
              >
                {totalDebit === totalCredit
                  ? 'Balanced. Amounts include GST.'
                  : `Out by ${fmt(totalDebit - totalCredit)}`}
              </td>
            </tr>
          </tfoot>
        </table>
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy || totalDebit !== totalCredit || totalDebit === 0n}>
          Post
        </button>
      </form>

      <h2>Recent journals</h2>
      {journals.error && <p className="error">{journals.error}</p>}
      {journals.data?.journals.map((j) => (
        <details key={j.id} className="journal">
          <summary>
            <span>{j.number ? `#${j.number}` : 'Draft'}</span>
            <span>{j.date}</span>
            <span className="grow">{j.memo ?? ''}</span>
            {j.reverses_journal_id && <span className="muted">reversal</span>}
            {j.reversed_by && <span className="muted">reversed</span>}
          </summary>
          <table className="table">
            <tbody>
              {j.lines.map((l) => {
                const a = accountById.get(l.accountId);
                return (
                  <tr key={l.lineNo}>
                    <td>
                      {a?.code} {a?.name}
                    </td>
                    <td className="num">{l.amount.startsWith('-') ? '' : l.amount}</td>
                    <td className="num">{l.amount.startsWith('-') ? l.amount.slice(1) : ''}</td>
                    <td>
                      {l.gstOfLineNo
                        ? `GST on line ${l.gstOfLineNo}`
                        : (l.taxCode?.replace(/^AU:/, '') ?? '')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {j.status === 'posted' && !j.reversed_by && (
            <button type="button" className="link" onClick={() => reverse(j.id)}>
              Reverse today
            </button>
          )}
        </details>
      ))}
    </section>
  );
}
